import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { loadRoutingConfigFromDatabase } from './services/routingEngineService';
import { saveWhatsAppConfig } from './services/whatsappService';
import { saveAIConfig } from './services/aiDispatchService';
import { saveGrokConfig } from './services/grokAIService';
import { saveEmailConfig } from './services/emailService';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Timeout wrapper for async operations
const withTimeout = (promise, timeoutMs = 10000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Operation timed out')), timeoutMs)
    )
  ]);
};

// Clear all local storage data related to the app
export const clearLocalData = () => {
  try {
    // Clear all hmgc_ prefixed keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('hmgc_') || key.startsWith('sb-') || key === 'preferredUnitSystem')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('Cleared local data:', keysToRemove);
    return true;
  } catch (e) {
    console.error('Error clearing local data:', e);
    return false;
  }
};

// Minimum password length. Supabase's project default is 6; if the project is
// configured stricter, updateUser() returns a server error that we surface as-is.
export const MIN_PASSWORD_LENGTH = 6;

// True when the current page load is a Supabase password-recovery landing.
// Implicit flow puts the recovery token in the URL hash (`type=recovery`); we
// also honor a `?recovery=true` query flag we add to redirectTo as insurance.
// An INVITE link (`type=invite`) is the same thing for a brand-new login: it must
// show the set-password form too, or the person can never choose a password.
// (Invites normally open hub.hmgc.io/set-password; this board is where Supabase
// sends them when that address is not in its allowed Redirect URLs.)
// Read synchronously so the first render can show the reset form with no flash
// of the dashboard.
export const isRecoveryUrl = () => {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  return hash.includes('type=recovery') ||
    hash.includes('type=invite') ||
    new URLSearchParams(search).get('recovery') === 'true';
};

// Remove Supabase recovery markers (`?recovery=true`, `#type=recovery`, or an
// `#error=...` left by an expired link) from the address bar, resetting to a
// clean root URL. Called on sign-out so a stale recovery URL can never
// re-trigger the ResetPassword landing after logout, and so recovery tokens
// don't linger in browser history. No-op when no marker is present, to avoid
// needlessly rewriting the URL on an ordinary logout.
export const clearRecoveryUrl = () => {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  const hasRecoveryMarker =
    hash.includes('type=recovery') ||
    hash.includes('type=invite') ||
    hash.includes('error') ||
    new URLSearchParams(search).get('recovery') === 'true';
  if (hasRecoveryMarker) {
    window.history.replaceState({}, document.title, window.location.origin + '/');
  }
};

// Role constants
export const ROLES = {
  OWNER: 'owner',
  DISPATCHER: 'dispatcher',
  DRIVER: 'driver',
  // __SHOP_ROLE__ HM Shop Desktop + the Shop app only; blocked from this board (App.js).
  SHOP: 'shop'
};

// Helper to check if user has a specific role
export const hasRole = (userRole, allowedRoles) => {
  if (!userRole) return false;
  if (Array.isArray(allowedRoles)) {
    return allowedRoles.includes(userRole);
  }
  return userRole === allowedRoles;
};

// Helper to check if user is owner
export const isOwner = (userRole) => userRole === ROLES.OWNER;

// Helper to check if user is dispatcher or owner
export const isDispatcherOrOwner = (userRole) => 
  userRole === ROLES.OWNER || userRole === ROLES.DISPATCHER;

const hydrateSettingsFromDatabase = async (userId) => {
  if (!userId) return;
  
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('settings')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.warn('Could not load settings from database:', error.message);
      return;
    }
    
    if (data?.settings) {
      if (data.settings.routingConfig) {
        await loadRoutingConfigFromDatabase(userId);
      }
      if (data.settings.whatsappConfig) {
        saveWhatsAppConfig(data.settings.whatsappConfig);
      }
      if (data.settings.aiDispatchConfig) {
        saveAIConfig(data.settings.aiDispatchConfig);
      }
      if (data.settings.grokConfig) {
        saveGrokConfig(data.settings.grokConfig);
      }
      if (data.settings.emailConfig) {
        saveEmailConfig(data.settings.emailConfig);
      }
      console.log('Settings hydrated from database');
    }
  } catch (err) {
    console.warn('Error hydrating settings from database:', err);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Seed from the URL so a recovery landing renders the reset form immediately,
  // before the async PASSWORD_RECOVERY event has a chance to fire.
  const [passwordRecovery, setPasswordRecovery] = useState(isRecoveryUrl);

  // Fetch user profile with role from profiles table
  const fetchProfile = async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // If profiles table doesn't exist or no profile found, 
        // check if this might be the first user (make them owner)
        console.log('Profile fetch error:', error.message);
        
        // Try to create a default profile for the user
        // New users always default to DISPATCHER for security
        // Owner role must be assigned manually in Supabase dashboard
        const defaultRole = ROLES.DISPATCHER;
        
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert([{
            id: userId,
            role: defaultRole,
            name: user?.email?.split('@')[0] || 'User',
            created_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError.message);
          setProfile({ id: userId, role: ROLES.DISPATCHER, name: 'User' });
          return { id: userId, role: ROLES.DISPATCHER, name: 'User' };
        }

        setProfile(newProfile);
        return newProfile;
      }

      // __SHOP_ROLE__ the hub sets the role by role_id; the old role text can lag behind (it stays
      // empty for a Shop / Fleet login until the database accepts 'shop'). A role_id that is the
      // shop role counts as shop here, so such a login is never shown the dashboard.
      let prof = data;
      if (prof && prof.role !== ROLES.SHOP && prof.role_id) {
        try {
          const { data: rr } = await supabase.from('roles').select('name').eq('id', prof.role_id).maybeSingle();
          if (rr && rr.name === ROLES.SHOP) prof = { ...prof, role: ROLES.SHOP };
        } catch (e) { /* the text role stands */ }
      }
      setProfile(prof);
      return prof;
    } catch (err) {
      console.error('Error in fetchProfile:', err);
      // Fallback to dispatcher role if anything fails
      setProfile({ id: userId, role: ROLES.DISPATCHER, name: 'User' });
      return { id: userId, role: ROLES.DISPATCHER, name: 'User' };
    }
  };

  useEffect(() => {
    // __CROSS_APP_SESSION__ (2026-09-13) Adopt a session handed over from billing.
    //
    // The handoff was one-way. This app gives ITS session to billing through the URL
    // (utils/auth.js -> ?token=&refresh_token=), and billing turns that pair into its
    // own httponly cookies. Nothing did the reverse, so a user authenticated in
    // billing whose board session had expired was asked to sign in again even though
    // they were already signed in one subdomain over -- the two apps hold separate
    // sessions (httponly cookies on billing.hmgc.io, localStorage on this origin) and
    // neither is visible to the other.
    //
    // The tokens are stripped from the address bar BEFORE the session is set, so they
    // do not sit in history or leak through a Referer header. They are single-use:
    // what persists afterwards is the session Supabase stores, never the URL. Any
    // other query params (redirect, action, recovery) are preserved untouched.
    const adoptHandoffSession = async () => {
      let params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
      const access_token = params.get('token');
      const refresh_token = params.get('refresh_token');
      if (!access_token || !refresh_token) return;
      params.delete('token');
      params.delete('refresh_token');
      const qs = params.toString();
      try {
        window.history.replaceState({}, document.title,
          window.location.pathname + (qs ? '?' + qs : ''));
      } catch (e) { /* non-fatal */ }
      try {
        await withTimeout(supabase.auth.setSession({ access_token, refresh_token }), 10000);
      } catch (e) {
        // An expired or rejected handoff is not an error worth blocking on -- fall
        // through to the normal getSession below, which shows the login form.
        console.warn('[auth] handoff session rejected:', e && e.message);
      }
    };

    // Check active sessions and sets the user with timeout protection
    const initAuth = async () => {
      try {
        await adoptHandoffSession();
        const { data: { session } } = await withTimeout(
          supabase.auth.getSession(),
          10000
        );
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (currentUser) {
          // Profile fetch is non-blocking - if it fails/times out, we still render the app
          try {
            await withTimeout(fetchProfile(currentUser.id), 10000);
            await hydrateSettingsFromDatabase(currentUser.id);
          } catch (profileError) {
            console.warn('Profile fetch failed/timed out, using fallback:', profileError.message);
            setProfile({ id: currentUser.id, role: ROLES.DISPATCHER, name: 'User' });
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        // If auth fails completely, clear any stale data and show login
        setUser(null);
        setProfile(null);
      } finally {
        // Always set loading to false to prevent infinite hang
        setLoading(false);
      }
    };

    initAuth();

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      // A recovery link establishes a temporary session and fires this event.
      // Flag it so App.js renders the set-new-password form instead of the app.
      if (_event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        try {
          await withTimeout(fetchProfile(currentUser.id), 10000);
          await hydrateSettingsFromDatabase(currentUser.id);
        } catch (profileError) {
          console.warn('Profile fetch failed on auth change:', profileError.message);
          setProfile({ id: currentUser.id, role: ROLES.DISPATCHER, name: 'User' });
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Refresh profile data
  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  // Send a password-reset email. Neutral by design: callers should not branch on
  // the result in a way that reveals whether the account exists.
  const sendPasswordReset = (email) =>
    supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/?recovery=true',
    });

  // Set a new password using the active recovery session. On success we drop the
  // recovery flag so the app falls through to its normal authenticated entry.
  const updatePassword = async (password) => {
    const result = await supabase.auth.updateUser({ password });
    if (!result.error) {
      setPasswordRecovery(false);
    }
    return result;
  };

  // Deterministic, self-contained logout. The in-app logout button must land on
  // <Login/> with NO manual refresh, so we flip local auth state and scrub any
  // recovery remnants synchronously BEFORE awaiting Supabase — the UI transition
  // never depends on the async SIGNED_OUT event (which can be delayed, or on an
  // expired token may not fire at all, leaving the dashboard mounted against a
  // dead session until a refresh). Order matters: clear React state first (App.js
  // falls through to Login on the next render), then revoke server-side while the
  // sb-* token is still in storage, then purge local data.
  const signOut = async () => {
    setPasswordRecovery(false);   // recovery flag can never survive into logout
    clearRecoveryUrl();           // and neither can a ?recovery=true / #type=recovery URL
    setUser(null);                // App.js: !user -> <Login/> immediately, no refresh
    setProfile(null);
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // Revocation can fail on an already-expired/absent session; local state is
      // already cleared above, so the user still lands cleanly on Login.
      console.warn('Supabase signOut revocation failed; local session already cleared:', e?.message);
    }
    clearLocalData();             // purge hmgc_/sb-/preferredUnitSystem (documented logout cleanup)
  };

  const value = {
    signUp: (data) => supabase.auth.signUp(data),
    signIn: (data) => supabase.auth.signInWithPassword(data),
    signOut,
    sendPasswordReset,
    updatePassword,
    passwordRecovery,
    user,
    profile,
    role: profile?.role || null,
    isOwner: profile?.role === ROLES.OWNER,
    isDispatcher: profile?.role === ROLES.DISPATCHER,
    isDriver: profile?.role === ROLES.DRIVER,
    canAccessDashboard: profile?.role === ROLES.OWNER || profile?.role === ROLES.DISPATCHER,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
